import { CircularProgress, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import numeral from 'numeral';
import React, { useEffect, useMemo, useState } from "react";

import { getStatsData } from "../api/data";
import { ExploreSchema } from "../core/global";
import { isBrowser } from "../helpers/common";
import { ErrorMessage } from "./error-message";
import { DownloadDataButton } from "./explore-download-data-button";
import { ExploreSelect } from "./explore-select";
import Loader from "./loader";

interface StatsData {
'1%': number,
'5%': number,
'10%': number,
'50%': number,
'90%': number,
'95%': number,
'99%': number,
'Max': number,
'Mean': number,
'Min': number,
'Std': number,
}

type MonthKeys = 'January' | 'February' | 'March' | 'April' | 'May' | 'June' | 'July' | 'August' | 'September' | 'October' | 'November' | 'December' | 'All-Year' | 'units' | 'vector'

export interface StatsDataTable {
  [key: string]: StatsData
}

interface ExploreStatisticsTableProps {
  exploreDataSchema: ExploreSchema | null;
}

export const ExploreStatisticsTable = ({
  exploreDataSchema,
}: ExploreStatisticsTableProps) => {
  const [keyOne, setKeyOne] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null)
  const [dataLoading, setDataLoading] = useState<boolean>(false)
  const [data, setData] = useState<StatsDataTable | null>(null)
  const [units, setUnits] = useState<string>('')

  // getStatsData
  useEffect(() => {
    setDataLoading(false)
    setError(null)
    const execute = async (preview: boolean, filename: string, vector: string) => {
      setDataLoading(true)
      
      const {provider = null, model = null, geography = null, version = null} = exploreDataSchema?.description || {}
      const result = await getStatsData({
        filename,
        preview,
        vector,
        orderId: exploreDataSchema?.description?.orderId || null,
        model,
        provider,
        geography,
        version,
      })
      const {error, response} = result

      if (error === 200 && response && typeof response === 'object' && Object.keys(response).length > 0) {
        const useData = {...response} as StatsDataTable
        if (Object.prototype.hasOwnProperty.call(useData, 'units') && typeof useData.units === 'string') {
          setUnits(useData.units)
          delete useData['units']
        } else {
          setUnits('')
        }
        setData(useData)
      } else {
        setError([`Problem loading stats data for **${keyOne}**.`])
      }
      setDataLoading(false)

    }
    if (exploreDataSchema && keyOne) {
      const {
        description: { filename, uid },
      } = exploreDataSchema;
      const preview = !!(uid && `${uid}`.indexOf('preview') !== -1)
      const vector = keyOne || ''
      execute(preview, filename, vector)
    }
  }, [exploreDataSchema, keyOne]);

  const variableOptions: { key: string, title: string }[] = useMemo(() => {
    if (exploreDataSchema) {
      const {
        vectors,
      } = exploreDataSchema;

      return [...vectors.map((v: any) => ({key: v.key, title: v.name}))];
    }
    return [];
  }, [exploreDataSchema, keyOne]);

  if (!isBrowser()) {
    return null;
  }

  return (
    <Container maxWidth={false} sx={{ mt: "16px", mb: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        {variableOptions && variableOptions.length > 0 && <ExploreSelect
          title="Variable"
          columnKeys={variableOptions || []}
          onChangeKey={(key) => {
            if (key && Array.isArray(key)) {
              setKeyOne(key[0]);
            } else {
              setKeyOne(null);
            }
          }}
        />}
        <div>
        {dataLoading ? 
          <CircularProgress size={24} /> 
        : 
          <>
          {data && 
            <DownloadDataButton
              title="Download statistics as CSV"
              data={Object.keys(data).map((Month, index) => {
                const row = data[Month] as StatsData
                return {
                  Month,
                  ...row,
                }
              })} 
            filename={`${keyOne} units: ${units}`}
            />}
          </>
        }
        </div>
      </div>

      {units && <Typography variant="body1" sx={{ mt: "16px", mb: '16px' }}>Units: {units}</Typography>}

      {error ? 
        <ErrorMessage message={error} />
        :
        (data && !dataLoading) ?
          <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: "black", width: "100%", whiteSpace: "nowrap" }}>
                    <TableCell style={{ color: "white" }}>Month</TableCell>
                    <TableCell style={{ color: "white" }}>Min</TableCell>
                    <TableCell style={{ color: "white" }}>Mean</TableCell>
                    <TableCell style={{ color: "white" }}>Max</TableCell>
                    <TableCell style={{ color: "white" }}>Std</TableCell>
                    <TableCell style={{ color: "white" }}>1%</TableCell>
                    <TableCell style={{ color: "white" }}>5%</TableCell>
                    <TableCell style={{ color: "white" }}>10%</TableCell>
                    <TableCell style={{ color: "white" }}>50%</TableCell>
                    <TableCell style={{ color: "white" }}>90%</TableCell>
                    <TableCell style={{ color: "white" }}>95%</TableCell>
                    <TableCell style={{ color: "white" }}>99%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {Object.keys(data).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">No data found</TableCell>
                      </TableRow>
                    ) : (
                      Object.keys(data).map((Month, index) => {
                        const row = data[Month] as StatsData
                        if (row) {
                          const {
                            '1%': p1,
                            '5%': p5,
                            '10%': p10,
                            '50%': p50,
                            '90%': p90,
                            '95%': p95,
                            '99%': p99,
                            'Max': max,
                            'Mean': mean,
                            'Min': min,
                            'Std': std,
                          } = row
                          return (
                            <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white" }}>
                              <TableCell>{Month}</TableCell>
                              <TableCell>{min === null ? '-' : numeral(min).format("0,0.00")}</TableCell>
                              <TableCell>{mean === null ? '-' : numeral(mean).format("0,0.00")}</TableCell>
                              <TableCell>{max === null ? '-' : numeral(max).format("0,0.00")}</TableCell>
                              <TableCell>{std === null ? '-' : numeral(std).format("0,0.00")}</TableCell>
                              <TableCell>{p1 === null ? '-' : numeral(p1).format("0,0.00")}</TableCell>
                              <TableCell>{p5 === null ? '-' : numeral(p5).format("0,0.00")}</TableCell>
                              <TableCell>{p10 === null ? '-' : numeral(p10).format("0,0.00")}</TableCell>
                              <TableCell>{p50 === null ? '-' : numeral(p50).format("0,0.00")}</TableCell>
                              <TableCell>{p90 === null ? '-' : numeral(p90).format("0,0.00")}</TableCell>
                              <TableCell>{p95 === null ? '-' : numeral(p95).format("0,0.00")}</TableCell>
                              <TableCell>{p99 === null ? '-' : numeral(p99).format("0,0.00")}</TableCell>
                          </TableRow>
                          )
                        }
                        return (
                          <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white" }}>
                            <TableCell>{Month}</TableCell>
                            {new Array(11).fill(null).map((_, index) => (
                              <TableCell key={index}>-</TableCell>
                            ))}
                        </TableRow>
                        )
                      })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
        :
        <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
          <Loader center showCog />
        </div>
      }

    </Container>
  );
};
